import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { Analytics } from "@vercel/analytics/react";

// Context
// AuthProvider is provided at the root in index.js
import { useAuth } from './context/AuthContext';

// Layout Components
import Header from './components/Header/Header';
import EmergencyBar from './components/Header/EmergencyBar';
import Footer from './components/Footer/Footer';
import ProtectedRoute from './components/ProtectedRoute';

// Componentes estáticos requeridos en render inicial
import Hero from './components/Hero/Hero';
import Services from './components/Services/Services';
import BusinessInfo from './components/BusinessInfo/BusinessInfo';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DebugAuthPage, TestXamppBackendPage, TestAuthEndpointPage } from './components/DebugPages';

// Page Components
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const ConfirmEmailChange = React.lazy(() => import('./pages/ConfirmEmailChange'));
const Maintenance = React.lazy(() => import('./pages/Maintenance'));
const NotFound = React.lazy(() => import('./pages/NotFound'));

const ColaboradoresAdmin = React.lazy(() => import('./pages/Admin/ColaboradoresAdmin'));
const ClientesAdmin = React.lazy(() => import('./pages/Admin/ClientesAdmin'));
const ServiciosAdmin = React.lazy(() => import('./pages/Admin/ServiciosAdmin'));
const NotificacionesAdmin = React.lazy(() => import('./pages/Admin/NotificacionesAdmin'));
const EmailTemplates = React.lazy(() => import('./pages/Admin/EmailTemplates'));
const RolesAdmin = React.lazy(() => import('./pages/Admin/RolesAdmin'));
const MembresiasAdmin = React.lazy(() => import('./pages/Admin/MembresiasAdmin'));
const MiembrosAdmin = React.lazy(() => import('./pages/Admin/MiembrosAdmin'));
const SolicitudesAdmin = React.lazy(() => import('./pages/Admin/SolicitudesAdmin'));
const SolicitudesMembresiaAdmin = React.lazy(() => import('./pages/Admin/SolicitudesMembresiaAdmin'));
const AddMember = React.lazy(() => import('./pages/Admin/AddMember'));
const EditMember = React.lazy(() => import('./pages/Admin/EditMember'));
const ScheduleView = React.lazy(() => import('./pages/Admin/ScheduleView'));
const EditService = React.lazy(() => import('./pages/Admin/EditService'));
const AddService = React.lazy(() => import('./pages/Admin/AddService'));
const Dashboard = React.lazy(() => import('./pages/Admin/Dashboard'));
const BoxesHub = React.lazy(() => import('./pages/Admin/BoxesHub'));
const BoxesAdmin = React.lazy(() => import('./pages/Admin/BoxesAdmin'));
const PersonasHub = React.lazy(() => import('./pages/Admin/PersonasHub'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const UserPanel = React.lazy(() => import('./pages/User/UserPanel'));
const UserDashboard = React.lazy(() => import('./pages/User/UserDashboard'));
const UserAppointments = React.lazy(() => import('./pages/User/UserAppointments'));
const UserServices = React.lazy(() => import('./pages/User/UserServices'));
const UserBooking = React.lazy(() => import('./pages/User/UserBooking'));
const UserProfile = React.lazy(() => import('./pages/User/UserProfile'));
const UserHistory = React.lazy(() => import('./pages/User/UserHistory'));
const CalendarioAdmin = React.lazy(() => import('./pages/Admin/CalendarioAdmin'));
// Admin HorariosBloqueados exists but collaborator has its own page
const ColabHorariosBloqueados = React.lazy(() => import('./pages/Colaborador/HorariosBloqueados'));
const MiPerfil = React.lazy(() => import('./pages/Admin/MiPerfil'));
const Marketing = React.lazy(() => import('./pages/Admin/Marketing'));
const Configuracion = React.lazy(() => import('./pages/Admin/Configuracion'));

// Collaborator Panel
const MemberPanel = React.lazy(() => import('./pages/Miembro/MemberPanel'));
const MemberDashboard = React.lazy(() => import('./pages/Miembro/Dashboard'));
const ReservarBox = React.lazy(() => import('./pages/Miembro/ReservarBox'));
const ComprarHoras = React.lazy(() => import('./pages/Miembro/ComprarHoras'));
const CollaboratorPanel = React.lazy(() => import('./pages/Colaborador/ColaboradorPanel'));
const ColabDashboard = React.lazy(() => import('./pages/Colaborador/Dashboard'));
const ColabMisCitas = React.lazy(() => import('./pages/Colaborador/MisCitas'));
const ColabMisPacientes = React.lazy(() => import('./pages/Colaborador/MisPacientes'));
// Using admin HorariosBloqueados page for collaborator as well
const ColabMiPerfil = React.lazy(() => import('./pages/Colaborador/MiPerfil'));
const ConfiguracionColaborador = React.lazy(() => import('./pages/Colaborador/Configuracion'));
const PacienteDetalle = React.lazy(() => import('./pages/Colaborador/PacienteDetalle'));
const PacienteFichasDashboard = React.lazy(() => import('./pages/Colaborador/PacienteFichasDashboard'));
const PacienteFichaRegistro = React.lazy(() => import('./pages/Colaborador/PacienteFichaRegistro'));
const Recursos = React.lazy(() => import('./pages/Colaborador/Recursos'));
const FormBuilder = React.lazy(() => import('./pages/Colaborador/FormBuilder'));
const CalendarioColaborador = React.lazy(() => import('./pages/Colaborador/CalendarioColaborador'));
const MisBoxes = React.lazy(() => import('./pages/Colaborador/MisBoxes'));
const MiHorario = React.lazy(() => import('./pages/Colaborador/MiHorario'));
const SolicitudesColaborador = React.lazy(() => import('./pages/Colaborador/Solicitudes'));

// Home Page Section Components

const TeamPage = React.lazy(() => import('./pages/Team.jsx'));
const ColaboradorProfile = React.lazy(() => import('./pages/ColaboradorProfile.jsx'));
const ServicesPage = React.lazy(() => import('./pages/Services'));
const SelectCollaborator = React.lazy(() => import('./pages/SelectCollaborator'));
const SelectDate = React.lazy(() => import('./pages/SelectDate'));
const ConfirmBooking = React.lazy(() => import('./pages/ConfirmBooking'));
const Contacto = React.lazy(() => import('./pages/Contacto'));
const MyAppointments = React.lazy(() => import('./pages/MyAppointments'));
const DirectBooking = React.lazy(() => import('./pages/DirectBooking'));




// Debug Pages


// Define the HomePage component that aggregates all its sections
const HomePage = () => (
  <main>
    <Hero />
    <Services />
    <BusinessInfo />
  </main>
);

const AppContent = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = React.useState(null);
  const [loadingSettings, setLoadingSettings] = React.useState(true);

  React.useEffect(() => {
    import('./services/api').then(({ getSettings }) => {
      getSettings()
        .then(({ data }) => {
          setSettings(data);

          // Inject Google Analytics if ID exists
          if (data?.seo_ga_id) {
            const script1 = document.createElement('script');
            script1.async = true;
            script1.src = `https://www.googletagmanager.com/gtag/js?id=${data.seo_ga_id}`;
            document.head.appendChild(script1);

            const script2 = document.createElement('script');
            script2.innerHTML = `
               window.dataLayer = window.dataLayer || [];
               function gtag(){dataLayer.push(arguments);}
               gtag('js', new Date());
               gtag('config', '${data.seo_ga_id}');
             `;
            document.head.appendChild(script2);
          }
        })
        .catch(console.error)
        .finally(() => setLoadingSettings(false));
    });
  }, []);

  const maintenanceEnabled = process.env.REACT_APP_MAINTENANCE_MODE === 'true' || settings?.system_maintenance_mode === 'true' || settings?.system_maintenance_mode === true;
  const isWhitelistedPath = ['/login', '/register', '/maintenance', '/forgot-password', '/reset-password', '/confirm-email'].some(p => location.pathname.startsWith(p));

  const noFooterPaths = ['/admin', '/clients', '/calendar', '/team', '/clientes', '/colaborador', '/user', '/usuario', '/miembro', '/equipo/turnos'];
  const showFooter = !noFooterPaths.some(path => location.pathname.startsWith(path));

  // Determine if main header should be shown
  const adminPaths = ['/admin', '/clients', '/calendar', '/team', '/clientes', '/user', '/colaborador', '/usuario', '/miembro'];
  const showHeader = !adminPaths.some(path => location.pathname.startsWith(path));

  // Maintenance redirect: if enabled and user not authenticated and not on whitelisted paths
  if (!loadingSettings && maintenanceEnabled && !isAuthenticated && !isWhitelistedPath) {
    return <Navigate to="/maintenance" replace />;
  } else if (process.env.REACT_APP_MAINTENANCE_MODE === 'true' && !isAuthenticated && !isWhitelistedPath) {
     return <Navigate to="/maintenance" replace />;
  }

  return (
    <div className="min-h-screen bg-background font-sans text-neutral-800">
      <EmergencyBar />
      {showHeader && <Header />}
      <React.Suspense fallback={<div className="flex h-screen items-center justify-center bg-white"><div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div></div>}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirm-email" element={<ConfirmEmailChange />} />
          <Route path="/reservar/servicios" element={<ServicesPage />} />
          <Route path="/reservar/colaborador" element={<SelectCollaborator />} />
          <Route path="/reservar/fecha" element={<SelectDate />} />
          <Route path="/reservar/confirmar" element={<ConfirmBooking />} />
          <Route path="/contacto" element={<Contacto />} />
          <Route path="/equipo" element={<TeamPage />} />
          <Route path="/equipo/:slug" element={<ColaboradorProfile />} />
          <Route path="/reservar/p/:slug" element={<DirectBooking />} />
          <Route path="/my-appointments" element={<ProtectedRoute allowedRoles={['admin', 'paciente', 'colaborador']}><MyAppointments /></ProtectedRoute>} />

          {/* Rutas de Panel de Usuario */}
          <Route element={<ProtectedRoute allowedRoles={['paciente', 'cliente']} />}>
            <Route path="/user" element={<UserPanel />}>
              <Route index element={<UserDashboard />} />
              <Route path="dashboard" element={<UserDashboard />} />
              <Route path="appointments" element={<Navigate to="/usuario/mis-citas" replace />} />
              <Route path="services" element={<UserServices />} />
              <Route path="booking" element={<UserBooking />} />
              <Route path="profile" element={<Navigate to="/usuario/mi-cuenta" replace />} />
              <Route path="configuracion" element={<Navigate to="/usuario/mi-cuenta" replace />} />
              <Route path="mi-cuenta" element={<UserProfile />} />
              <Route path="history" element={<Navigate to="/usuario/historial" replace />} />
            </Route>
            <Route path="/usuario" element={<UserPanel />}>
              <Route index element={<Navigate to="mis-citas" replace />} />
              <Route path="mis-citas" element={<UserAppointments />} />
              <Route path="historial" element={<UserHistory />} />
              <Route path="configuracion" element={<Navigate to="/usuario/mi-cuenta" replace />} />
              <Route path="mi-cuenta" element={<UserProfile />} />
            </Route>
          </Route>

          {/* Rutas de Panel de Colaborador */}
          <Route element={<ProtectedRoute allowedRoles={['colaborador', 'admin']} />}>
            <Route path="/colaborador" element={<CollaboratorPanel />}>
              <Route index element={<ColabDashboard />} />
              <Route path="dashboard" element={<ColabDashboard />} />
              <Route path="solicitudes" element={<SolicitudesColaborador />} />
              <Route path="calendario" element={<CalendarioColaborador />} />
              <Route path="mis-citas" element={<ColabMisCitas />} />
              <Route path="mis-pacientes" element={<ColabMisPacientes />} />
              <Route path="mis-solicitudes" element={<MisBoxes />} />
              <Route path="mi-horario" element={<MiHorario />} />
              <Route path="pacientes/:id" element={<PacienteDetalle />} />
              <Route path="pacientes/:id/fichas" element={<PacienteFichasDashboard />} />
              <Route path="pacientes/:id/fichas/registro" element={<PacienteFichaRegistro />} />
              <Route path="pacientes/:id/fichas/:appointmentId" element={<PacienteFichaRegistro />} />
              <Route path="recursos" element={<Recursos />} />
              <Route path="recursos/formularios/nuevo" element={<FormBuilder />} />
              <Route path="recursos/formularios/nuevo/:pacienteId" element={<FormBuilder />} />
              <Route path="horarios-bloqueados" element={<ColabHorariosBloqueados />} />
              <Route path="reservar" element={<ReservarBox />} />
              <Route path="mi-perfil" element={<ColabMiPerfil />} />
              <Route path="configuracion" element={<ConfiguracionColaborador />} />
              <Route path="mi-cuenta" element={<ConfiguracionColaborador />} />
            </Route>
          </Route>

          {/* Rutas de Panel de Miembro */}
          <Route element={<ProtectedRoute allowedRoles={['miembro']} />}>
            <Route path="/miembro" element={<MemberPanel />}>
              <Route index element={<MemberDashboard />} />
              <Route path="dashboard" element={<MemberDashboard />} />
              <Route path="mis-solicitudes" element={<MisBoxes />} />
              <Route path="mi-horario" element={<MiHorario />} />
              <Route path="mi-perfil" element={<ColabMiPerfil />} />
              <Route path="calendario" element={<CalendarioColaborador />} />
              <Route path="comprar-horas" element={<ComprarHoras />} />
              <Route path="reservar" element={<ReservarBox />} />
            </Route>
          </Route>

          {/* Rutas de administración */}
          <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
            <Route path="/admin" element={<AdminPanel />}>
              <Route index element={<Dashboard />} />
              <Route path="mi-cuenta" element={<MiPerfil />} />
            </Route>

            <Route path="/admin/perfil" element={<AdminPanel />}>
              <Route index element={<MiPerfil />} />
            </Route>

            <Route path="/admin/marketing" element={<AdminPanel />}>
              <Route index element={<Marketing />} />
            </Route>
            <Route path="/admin/configuracion" element={<AdminPanel />}>
              <Route index element={<Configuracion />} />
            </Route>

            {/* English route mappings */}
            {/* Team Routes (Spanish/English) */}
            <Route path="/admin/personas" element={<AdminPanel />}>
              <Route index element={<PersonasHub />} />
            </Route>
            <Route path="/admin/colaboradores" element={<AdminPanel />}>
              <Route index element={<ColaboradoresAdmin />} />
              <Route path="nuevo" element={<AddMember />} />
              <Route path="editar/:memberId" element={<EditMember />} />
            </Route>
            <Route path="/equipo/miembros" element={<Navigate to="/admin/colaboradores" replace />} />
            <Route path="/team/members" element={<Navigate to="/admin/colaboradores" replace />} />
            <Route path="/team/members/new" element={<Navigate to="/admin/colaboradores/nuevo" replace />} />
            <Route path="/team/members/edit/:memberId" element={<Navigate to="/admin/colaboradores/editar/:memberId" replace />} />

            {/* Schedule Routes */}
            <Route path="/equipo/turnos" element={<AdminPanel />}>
              <Route index element={<ScheduleView />} />
            </Route>
            <Route path="/team/schedule" element={<Navigate to="/equipo/turnos" replace />} />

            <Route path="/admin/calendario" element={<AdminPanel />}>
              <Route index element={<CalendarioAdmin />} />
            </Route>

            <Route path="/admin/notificaciones" element={<AdminPanel />}>
              <Route index element={<NotificacionesAdmin />} />
            </Route>

            <Route path="/admin/plantillas" element={<AdminPanel />}>
              <Route index element={<EmailTemplates />} />
            </Route>

            <Route path="/admin/roles" element={<AdminPanel />}>
              <Route index element={<RolesAdmin />} />
            </Route>

            <Route path="/admin/clientes" element={<AdminPanel />}>
              <Route index element={<ClientesAdmin />} />
            </Route>

            <Route path="/admin/boxes" element={<AdminPanel />}>
              <Route index element={<BoxesHub />} />
            </Route>
            <Route path="/admin/gestion-boxes" element={<AdminPanel />}>
              <Route index element={<BoxesAdmin />} />
            </Route>

            <Route path="/services" element={<Navigate to="/admin/servicios" replace />} />

            <Route path="/admin/servicios" element={<AdminPanel />}>
              <Route index element={<ServiciosAdmin />} />
              <Route path="crear" element={<AddService />} />
              <Route path="editar/:serviceId" element={<EditService />} />
            </Route>

            <Route path="/admin/membresias" element={<AdminPanel />}>
              <Route index element={<MembresiasAdmin />} />
            </Route>

            <Route path="/admin/miembros" element={<AdminPanel />}>
              <Route index element={<MiembrosAdmin />} />
            </Route>

            <Route path="/admin/solicitudes" element={<AdminPanel />}>
              <Route index element={<SolicitudesAdmin />} />
            </Route>

            <Route path="/admin/solicitudes-membresia" element={<AdminPanel />}>
              <Route index element={<SolicitudesMembresiaAdmin />} />
            </Route>

            <Route path="/services/products" element={<AdminPanel />}>
              <Route index element={<div>Página de Productos</div>} />
            </Route>

            <Route path="/inventory/list" element={<AdminPanel />}>
              <Route index element={<div>Página de Inventarios</div>} />
            </Route>

            <Route path="/inventory/stock-orders" element={<AdminPanel />}>
              <Route index element={<div>Página de Pedidos de stock</div>} />
            </Route>

            <Route path="/inventory/suppliers" element={<AdminPanel />}>
              <Route index element={<div>Página de Proveedores</div>} />
            </Route>
          </Route>

          {/* Rutas de Debug - Archivos HTML estáticos */}
          <Route path="/terminos-y-condiciones" element={<TermsAndConditions />} />
          <Route path="/politica-de-privacidad" element={<PrivacyPolicy />} />
          <Route path="/debug_auth.html" element={<DebugAuthPage />} />
          <Route path="/test_xampp_backend.html" element={<TestXamppBackendPage />} />
          <Route path="/test_auth_endpoint.html" element={<TestAuthEndpointPage />} />
          {/* Ruta para capturar cualquier enlace inexistente (404) */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </React.Suspense>
      {showFooter && <Footer />}
    </div >
  );
};

function App() {
  return (
    <Router>
      <AppContent />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />


      <Analytics />
    </Router>
  );
}

export default App;






